import { useMemo, useState, useCallback, useEffect } from 'react'
import superagent from 'superagent'
import { ChevronDown, ChevronRight } from 'lucide-react'
import { ToastContainer, toast } from 'react-toastify'
import { backendUrl } from '@helpers/consts'

import { Card, CardContent as CardBody } from '@shadcn/components/ui/card'
import { Button } from '@shadcn/components/ui/button'
import { Input } from '@shadcn/components/ui/input'
import { Badge } from '@shadcn/components/ui/badge'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@shadcn/components/ui/collapsible'
import ReactSelect from '@shadcn/components/ui/react-select'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@shadcn/components/ui/dialog'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@shadcn/components/ui/tabs'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@shadcn/components/ui/tooltip'
import {
  CONTAINER_TREE_ROOT_MAX_TIER,
  EMPTY_FORM,
  EMPTY_SUPPLEMENTARY_ROOTS,
  LABEL_CAN_PACK_INTO,
  MAX_TREE_DEPTH,
  PREFIX_FILTER_ALL,
  SEARCH_SUPPLEMENTARY_MIN_TIER,
  TREE_BASE_PAD,
  TREE_DEPTH_PX,
  TREE_ROW_GRID_CLASS,
  TREE_ROW_GRID_WITH_TIER,
  TREE_ROW_GRID_WITHOUT_TIER,
} from './constants'
import {
  compareRulesBySizeOrder,
  getRuleSizeOrder,
  getTierBadgeStyle,
  getTierLabel,
  getTierStyle,
  sortPrefixes,
  sortRelationshipPrefixes,
  derivedLabelFor,
  productCount,
  inverseRelationships,
} from './helpers'
import {
  PrefixWrapList,
  PrefixMultiSelect,
  TierBadgeOnly,
} from './form-controls'
import './index.scss'

const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem('authJWT')}` })

const getPermissionKey = (permission) => {
  if (typeof permission === 'string') return permission
  if (!permission || typeof permission !== 'object') return null
  return permission.key || permission.name || permission.permission || permission.value || null
}

const getAuthPermissionSet = () => {
  try {
    const user = JSON.parse(localStorage.getItem('authUser') || '{}')
    const rawPermissions = [
      ...(Array.isArray(user.permissions) ? user.permissions : []),
      ...(Array.isArray(user.role?.permissions) ? user.role.permissions : []),
      ...(Array.isArray(user.roles) ? user.roles.flatMap((role) => role?.permissions || []) : []),
    ]
    return new Set(rawPermissions.map(getPermissionKey).filter(Boolean))
  } catch {
    return new Set()
  }
}

const formatSeedPrefixList = (prefixes = []) => {
  if (!prefixes.length) return ''
  const visible = prefixes.slice(0, 8).join(', ')
  return prefixes.length > 8 ? ` (${visible}, +${prefixes.length - 8} more)` : ` (${visible})`
}

const formatSeedToast = (result = {}) => {
  const created = result.created ?? result.upserted ?? 0
  const deleted = result.deleted ?? 0
  const parts = []
  if (created) parts.push(`${created} created${formatSeedPrefixList(result.createdPrefixes)}`)
  if (deleted) parts.push(`${deleted} deleted${formatSeedPrefixList(result.deletedPrefixes)}`)
  return parts.length ? `Seed complete: ${parts.join(', ')}` : 'Seed complete: no prefix changes'
}

const getCapacityHintTitle = (tier, capacityHintsByTier, tierLabels) => {
  const hints = (capacityHintsByTier?.[tier] || capacityHintsByTier?.[String(tier)] || [])
    .filter((hint) => hint && hint.max !== 0)

  const lines = ['Capacity from runtime logic:']
  if (hints.length) {
    for (const hint of hints) {
      const tierLabel = getTierLabel(tierLabels, hint.targetTier).split(' - ')[0]
      lines.push(`${tierLabel}: ${hint.max == null ? 'not capped directly' : `max ${hint.max}`}`)
    }
  } else {
    lines.push('No capacity for nested items.')
  }

  if (String(tier) === '1') {
    lines.push('Personalized PLS does not allow checked luggage inside.')
  }
  if (String(tier) === '4') {
    lines.push('Some soft bags cannot contain anything.')
  }
  lines.push('If capacity is exceeded, the item stays outside this container and may nest elsewhere or ship separately.')
  return lines.join('\n')
}

function CapacityHintTooltip ({ tier, capacityHintsByTier, tierLabels, children }) {
  const lines = getCapacityHintTitle(tier, capacityHintsByTier, tierLabels).split('\n').filter(Boolean)
  const [title, ...details] = lines

  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="inline-flex max-w-full">
            {children}
          </span>
        </TooltipTrigger>
        <TooltipContent
          side="top"
          align="start"
          className="max-w-[24rem] rounded-md border border-gray-200 bg-white px-3 py-2 text-left text-xs leading-5 text-gray-700 shadow-lg"
        >
          <div className="mb-1 font-semibold text-gray-900">{title}</div>
          <div className="space-y-0.5">
            {details.map((line) => (
              <div key={line}>{line}</div>
            ))}
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}
function TreeRowActions ({ canMutateRow, onEdit, onDelete }) {
  if (!canMutateRow) return null
  return (
    <div className="absolute right-3 top-1/2 flex w-40 -translate-y-1/2 flex-row flex-nowrap items-center justify-end gap-1">
      <Button type="button" variant="outline" size="sm" className="h-7 text-xs" onClick={(e) => { e.stopPropagation(); onEdit() }}>Edit</Button>
      <Button type="button" variant="destructive" size="sm" className="h-7 text-xs" onClick={(e) => { e.stopPropagation(); onDelete() }}>Delete</Button>
    </div>
  )
}

function SpecialPrefixBadge ({ prefix, canEdit, onEdit, variant = 'outline' }) {
  const badge = <Badge variant={variant} className="font-mono text-[11px] text-black">{prefix}</Badge>
  if (!canEdit) return badge
  return (
    <button type="button" onClick={() => onEdit(prefix)} title={`Edit ${prefix}`} className="cursor-pointer rounded hover:ring-1 hover:ring-gray-300">
      {badge}
    </button>
  )
}

const getVisibleNestedPrefixes = ({
  contained,
  treeRootKind,
  hasTierFilter,
  prefixHasTierMatch,
  isDirectTierMatch,
  activeSearchTerm,
  prefixHasSearchMatch,
  isDirectSearchMatch,
}) => {
  if (treeRootKind === 'supplementary') {
    if (hasTierFilter && prefixHasTierMatch && !isDirectTierMatch) {
      return contained.filter((childPrefix) => prefixHasTierMatch(childPrefix))
    }
    return [...contained]
  }
  if (activeSearchTerm && prefixHasSearchMatch && !isDirectSearchMatch) {
    return contained.filter((childPrefix) => prefixHasSearchMatch(childPrefix))
  }
  if (hasTierFilter && prefixHasTierMatch && !isDirectTierMatch) {
    return contained.filter((childPrefix) => prefixHasTierMatch(childPrefix))
  }
  return contained
}

function PrefixChildChip ({
  childPrefix,
  depth,
  ancestorPrefixes,
  ruleByPrefix,
  defaultExpandedDepth,
  tierLabels,
  searchTerm,
  prefixHasSearchMatch,
  searchMatchedAncestor,
  tierFilter,
  prefixHasTierMatch,
  treeRootKind,
}) {
  const chain = ancestorPrefixes ?? []
  const isCircularRef = chain.includes(childPrefix)
  const childRule = ruleByPrefix.get(childPrefix)
  const isInactive = childRule?.active === false
  const contained = [...(childRule?.nestingRelationships || [])].filter(Boolean)
  const activeSearchTerm = searchMatchedAncestor ? '' : searchTerm
  const isDirectSearchMatch = !!activeSearchTerm && String(childPrefix || '').toUpperCase().includes(activeSearchTerm)
  const hasDescendantSearchMatch = !!activeSearchTerm && contained.some((prefix) => prefixHasSearchMatch?.(prefix))
  const hasTierFilter = tierFilter !== 'all'
  const isDirectTierMatch = hasTierFilter && String(childRule?.tier) === tierFilter
  const hasDescendantTierMatch = hasTierFilter && contained.some((prefix) => prefixHasTierMatch?.(prefix))
  const visibleContained = getVisibleNestedPrefixes({
    contained,
    treeRootKind,
    hasTierFilter,
    prefixHasTierMatch,
    isDirectTierMatch,
    activeSearchTerm,
    prefixHasSearchMatch,
    isDirectSearchMatch,
  })
  const hasChildrenList = visibleContained.length > 0 && depth < MAX_TREE_DEPTH
  const hasExpandPreset = typeof defaultExpandedDepth === 'number'
  const targetOpenFromPreset =
    hasExpandPreset
      ? depth < defaultExpandedDepth
      : activeSearchTerm
      ? hasDescendantSearchMatch && !isDirectSearchMatch
      : hasTierFilter
        ? hasDescendantTierMatch && !isDirectTierMatch
        : depth < 2
  const [open, setOpen] = useState(targetOpenFromPreset)

  useEffect(() => {
    setOpen(targetOpenFromPreset)
  }, [targetOpenFromPreset])

  if (isCircularRef) {
    return (
      <div className="max-w-full rounded-md border border-slate-200 bg-slate-50 px-2.5 py-1.5 text-xs text-slate-700">
        <span className="font-mono font-semibold">{childPrefix}</span>
        {' '}
        <span className="text-slate-600">circular ref</span>
      </div>
    )
  }

  const content = (
    <>
      {hasChildrenList ? (
        open ? (
          <ChevronDown className="h-3.5 w-3.5 shrink-0 text-slate-600" aria-hidden />
        ) : (
          <ChevronRight className="h-3.5 w-3.5 shrink-0 text-slate-600" aria-hidden />
        )
      ) : null}
      <span className={['truncate font-mono font-semibold', isInactive ? 'text-gray-500' : 'text-black'].join(' ')}>
        {childPrefix}
      </span>
      {isInactive ? (
        <span className="rounded bg-gray-200 px-1.5 py-0.5 text-[10px] font-medium text-gray-600">
          inactive
        </span>
      ) : null}
      {contained.length ? (
        <Badge variant="secondary" className="ml-0.5 whitespace-nowrap font-mono text-[10px] text-black tabular-nums">
          {contained.length}
          {' '}
          {contained.length === 1 ? 'child' : 'children'}
        </Badge>
      ) : null}
    </>
  )

  if (hasChildrenList && open) {
    return (
      <div className="basis-full rounded-lg border border-slate-200 bg-white p-2 shadow-sm">
        <button
          type="button"
          className="flex max-w-full items-center gap-1.5 rounded-md border border-slate-200 bg-slate-50 px-2.5 py-1.5 text-left text-xs hover:bg-slate-100"
          onClick={() => setOpen(false)}
          aria-expanded={open}
          aria-label={`Collapse nesting for ${childPrefix}`}
        >
          {content}
        </button>
        <div className="mt-2 border-l-2 border-slate-200 pl-3">
          <PrefixChildrenGroups
            prefixes={visibleContained}
            parentPrefix={childPrefix}
            depth={depth + 1}
            ancestorPrefixes={[...chain, childPrefix]}
            ruleByPrefix={ruleByPrefix}
            defaultExpandedDepth={defaultExpandedDepth}
            tierLabels={tierLabels}
            searchTerm={searchTerm}
            prefixHasSearchMatch={prefixHasSearchMatch}
            searchMatchedAncestor={searchMatchedAncestor || isDirectSearchMatch}
            tierFilter={tierFilter}
            prefixHasTierMatch={prefixHasTierMatch}
            treeRootKind={treeRootKind}
          />
        </div>
      </div>
    )
  }

  return (
    <div className="flex max-w-full items-center">
      {hasChildrenList ? (
        <button
          type="button"
          className="flex min-h-9 max-w-full items-center gap-1.5 rounded-md border border-slate-200 bg-white px-2.5 py-1.5 text-left text-xs shadow-sm hover:bg-slate-50"
          onClick={() => setOpen((value) => !value)}
          aria-expanded={open}
          aria-label={`${open ? 'Collapse' : 'Expand'} nesting for ${childPrefix}`}
        >
          {content}
        </button>
      ) : (
        <div className="flex min-h-9 max-w-full items-center gap-1.5 rounded-md border border-slate-200 bg-white px-2.5 py-1.5 text-xs shadow-sm">
          {content}
        </div>
      )}
    </div>
  )
}

function PrefixChildrenGroups ({
  prefixes,
  parentPrefix,
  depth,
  ancestorPrefixes,
  ruleByPrefix,
  defaultExpandedDepth,
  tierLabels,
  searchTerm,
  prefixHasSearchMatch,
  searchMatchedAncestor,
  tierFilter,
  prefixHasTierMatch,
  treeRootKind,
}) {
  const groups = useMemo(() => {
    const byTier = new Map()
    for (const prefix of sortRelationshipPrefixes(prefixes, ruleByPrefix)) {
      const rule = ruleByPrefix.get(prefix)
      const tierKey = rule?.tier != null && rule.tier !== '' ? String(rule.tier) : 'unknown'
      if (!byTier.has(tierKey)) {
        byTier.set(tierKey, {
          tier: rule?.tier,
          label: rule?.tier != null && rule.tier !== '' ? getTierLabel(tierLabels, rule.tier) : 'Unknown tier',
          prefixes: [],
        })
      }
      byTier.get(tierKey).prefixes.push(prefix)
    }
    return [...byTier.values()]
      .map((group) => ({
        ...group,
        prefixes: sortRelationshipPrefixes(group.prefixes, ruleByPrefix),
      }))
      .sort((a, b) => {
        const at = Number(a.tier)
        const bt = Number(b.tier)
        const aRank = Number.isFinite(at) ? at : 999
        const bRank = Number.isFinite(bt) ? bt : 999
        return aRank - bRank || String(a.label).localeCompare(String(b.label))
      })
  }, [prefixes, ruleByPrefix, tierLabels])

  if (!prefixes.length) return null

  return (
    <div className="space-y-2 py-2 pr-3">
      {groups.map((group) => {
        const tierStyle = getTierStyle(group.tier)
        return (
        <section
          key={`${parentPrefix}-${group.label}`}
          className="rounded-lg border bg-white p-2"
          style={{ borderColor: tierStyle.border, backgroundColor: tierStyle.panel }}
        >
          <div className="mb-2 flex min-w-0 items-center gap-2 border-b border-slate-100 pb-2 text-xs text-slate-600">
            <Badge
              variant="outline"
              className="max-w-full truncate text-[11px]"
              style={getTierBadgeStyle(group.tier)}
            >
              {group.label}
            </Badge>
            <Badge variant="secondary" className="whitespace-nowrap font-mono text-[11px] text-black tabular-nums">
              {group.prefixes.length}
              {' '}
              {group.prefixes.length === 1 ? 'child' : 'children'}
            </Badge>
          </div>
          <div className="flex min-w-0 flex-wrap items-start gap-2">
            {group.prefixes.map((childPrefix) => (
              <PrefixChildChip
                key={`${parentPrefix}->${childPrefix}`}
                childPrefix={childPrefix}
                depth={depth}
                ancestorPrefixes={ancestorPrefixes}
                ruleByPrefix={ruleByPrefix}
                defaultExpandedDepth={defaultExpandedDepth}
                tierLabels={tierLabels}
                searchTerm={searchTerm}
                prefixHasSearchMatch={prefixHasSearchMatch}
                searchMatchedAncestor={searchMatchedAncestor}
                tierFilter={tierFilter}
                prefixHasTierMatch={prefixHasTierMatch}
                treeRootKind={treeRootKind}
              />
            ))}
          </div>
        </section>
        )
      })}
    </div>
  )
}

/**
 * Recursive container tree row: subtree follows each rule’s `nestingRelationships`.
 */
function PrefixTreeBranch ({
  rule,
  prefix: prefixProp,
  depth,
  ancestorPrefixes,
  ruleByPrefix,
  effectiveSource,
  canManageNestingRules,
  openEdit,
  handleDelete,
  defaultExpandedDepth,
  tierLabels,
  searchTerm,
  prefixHasSearchMatch,
  searchMatchedAncestor = false,
  tierFilter,
  prefixHasTierMatch,
  ancestorParentByPrefix,
  supplementaryRootPrefixes,
  prefixProducts,
  /** `anchor`: search-hit tree roots; stubs under here have no expand. `supplementary`: editable duplicate roots below — full subtree expands here. */
  treeRootKind = 'anchor',
  parentPrefix = null,
  showTier = true,
}) {
  const resolved = rule || (prefixProp ? { prefix: prefixProp, tier: undefined, flags: {}, active: true } : null)
  const prefix = resolved?.prefix
  const supplementaryRoots = supplementaryRootPrefixes ?? EMPTY_SUPPLEMENTARY_ROOTS
  const isListedSupplementaryEditableRoot = depth === 0 && supplementaryRoots.size > 0 && supplementaryRoots.has(prefix)
  const isForcedStubUnderAnchorSearchTree =
    treeRootKind === 'anchor' && depth > 0 && supplementaryRoots.size > 0 && supplementaryRoots.has(prefix)
  const isReferenceRow =
    !isListedSupplementaryEditableRoot &&
    (isForcedStubUnderAnchorSearchTree ||
      (ancestorParentByPrefix?.has(prefix) && ancestorParentByPrefix.get(prefix) !== parentPrefix))
  const contained = [...(resolved?.nestingRelationships || [])].filter(Boolean)
  const activeSearchTerm = searchMatchedAncestor ? '' : searchTerm
  const isDirectSearchMatch = !!activeSearchTerm && String(prefix || '').toUpperCase().includes(activeSearchTerm)
  const hasDescendantSearchMatch = !!activeSearchTerm && contained.some((childPrefix) => prefixHasSearchMatch?.(childPrefix))
  const hasTierFilter = tierFilter !== 'all'
  const isDirectTierMatch = hasTierFilter && String(resolved?.tier) === tierFilter
  const hasDescendantTierMatch = hasTierFilter && contained.some((childPrefix) => prefixHasTierMatch?.(childPrefix))

  const hasExpandPreset = typeof defaultExpandedDepth === 'number'
  const targetOpenFromPreset =
    hasExpandPreset
      ? depth < defaultExpandedDepth
      : activeSearchTerm
      ? hasDescendantSearchMatch && !isDirectSearchMatch
      : hasTierFilter
        ? hasDescendantTierMatch && !isDirectTierMatch
      : depth < 2
  const [branchOpen, setBranchOpen] = useState(targetOpenFromPreset)

  useEffect(() => {
    setBranchOpen(
      hasExpandPreset
        ? depth < defaultExpandedDepth
        : activeSearchTerm
        ? hasDescendantSearchMatch && !isDirectSearchMatch
        : hasTierFilter
          ? hasDescendantTierMatch && !isDirectTierMatch
        : depth < 2,
    )
  }, [
    defaultExpandedDepth,
    depth,
    hasDescendantSearchMatch,
    hasDescendantTierMatch,
    hasTierFilter,
    isDirectSearchMatch,
    isDirectTierMatch,
    activeSearchTerm,
    hasExpandPreset,
  ])

  if (!resolved) return null

  const ids = ancestorPrefixes ?? []
  const visibleContained = getVisibleNestedPrefixes({
    contained,
    treeRootKind,
    hasTierFilter,
    prefixHasTierMatch,
    isDirectTierMatch,
    activeSearchTerm,
    prefixHasSearchMatch,
    isDirectSearchMatch,
  })
  const hasChildrenList = !isReferenceRow && visibleContained.length > 0 && depth < MAX_TREE_DEPTH
  const tier = resolved.tier
  const isInactive = resolved.active === false
  /** Direct children count from NESTING_RELATIONSHIPS — also on reference stubs under another branch. */
  const childrenBadgeCount = contained.length
  const showChildrenBadge = childrenBadgeCount > 0
  /** Only roots of the containers list (`depth === 0`) — nested rows are browse-only; edit the same prefix on its root row outside the tree. */
  const canMutateRow =
    canManageNestingRules &&
    depth === 0 &&
    effectiveSource === 'database' &&
    !resolved.readonlyFromConstants &&
    resolved._id

  const leftPad = TREE_BASE_PAD + depth * TREE_DEPTH_PX

  const gridRow = (
    chevronSlot,
  ) => (
    <div
      className={TREE_ROW_GRID_CLASS}
      style={{
        paddingLeft: leftPad,
        gridTemplateColumns: showTier ? TREE_ROW_GRID_WITH_TIER : TREE_ROW_GRID_WITHOUT_TIER,
      }}
    >
      {chevronSlot}
      <div className="flex min-w-0 items-center gap-2">
        <span
          className={['block min-w-0 shrink-0 truncate font-mono font-semibold text-sm tabular-nums', isInactive ? 'text-gray-500' : 'text-black'].join(' ')}
          title={prefix}
        >
          {prefix}
        </span>
        {derivedLabelFor(prefix, prefixProducts) ? (
          <span className="min-w-0 truncate text-[11px] text-gray-500">· {derivedLabelFor(prefix, prefixProducts)}</span>
        ) : null}
        {isInactive ? (
          <span className="shrink-0 rounded bg-gray-200 px-1.5 py-0.5 text-[10px] font-medium text-gray-600">
            inactive
          </span>
        ) : null}
      </div>
      {showTier ? <TierBadgeOnly tier={tier} tierLabels={tierLabels} /> : null}
      <div className="flex min-h-7 min-w-0 items-center justify-start">
        {showChildrenBadge ? (
          <Badge variant="secondary" className="whitespace-nowrap font-mono text-[11px] text-black tabular-nums">
            {childrenBadgeCount}
            {' '}
            {childrenBadgeCount === 1 ? 'child' : 'children'}
          </Badge>
        ) : null}
      </div>
      <TreeRowActions
        canMutateRow={canMutateRow}
        onEdit={() => openEdit(rule || ruleByPrefix.get(prefix))}
        onDelete={() => handleDelete((rule || ruleByPrefix.get(prefix))?._id)}
      />
    </div>
  )

  const rowBlock = (chevronSlot) => (
    <div className={[
      'relative flex w-full flex-col border-b border-gray-100 hover:bg-gray-50',
      isInactive ? 'bg-gray-50 text-gray-500' : '',
    ].join(' ')}>
      <div className="overflow-x-auto">{gridRow(chevronSlot)}</div>
    </div>
  )

  if (!hasChildrenList) {
    return (
      rowBlock(
        <span className="flex h-7 w-full shrink-0 items-center justify-center" aria-hidden />,
      )
    )
  }

  return (
    <Collapsible open={branchOpen} onOpenChange={setBranchOpen}>
      {rowBlock(
        <CollapsibleTrigger
          type="button"
          aria-expanded={branchOpen}
          aria-label={`${branchOpen ? 'Collapse' : 'Expand'} nesting for ${prefix}`}
          className="relative z-[1] flex h-7 w-full shrink-0 cursor-pointer items-center justify-center rounded text-gray-700 outline-none hover:bg-gray-200 focus-visible:ring-2 focus-visible:ring-gray-400"
        >
          {branchOpen ? (
            <ChevronDown className="pointer-events-none h-4 w-4 shrink-0" aria-hidden />
          ) : (
            <ChevronRight className="pointer-events-none h-4 w-4 shrink-0" aria-hidden />
          )}
        </CollapsibleTrigger>,
      )}
      <CollapsibleContent>
        <div className="ml-5 border-l border-gray-200 bg-slate-50/30 pl-4">
          <PrefixChildrenGroups
            prefixes={visibleContained}
            parentPrefix={prefix}
            depth={depth + 1}
            ancestorPrefixes={[...ids, prefix]}
            ruleByPrefix={ruleByPrefix}
            defaultExpandedDepth={defaultExpandedDepth}
            tierLabels={tierLabels}
            searchTerm={searchTerm}
            prefixHasSearchMatch={prefixHasSearchMatch}
            searchMatchedAncestor={searchMatchedAncestor || isDirectSearchMatch}
            tierFilter={tierFilter}
            prefixHasTierMatch={prefixHasTierMatch}
            treeRootKind={treeRootKind}
          />
        </div>
      </CollapsibleContent>
    </Collapsible>
  )
}

const NestingRulesPage = () => {
  const [rules, setRules] = useState([])
  const [effectiveSource, setEffectiveSource] = useState('database')
  const [loading, setLoading] = useState(true)
  const [tierFilter, setTierFilter] = useState('all')
  const [search, setSearch] = useState('')
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editId, setEditId] = useState(null)
  const [form, setForm] = useState({ ...EMPTY_FORM })
  const [saving, setSaving] = useState(false)
  const [seeding, setSeeding] = useState(false)
  const [seedDialogOpen, setSeedDialogOpen] = useState(false)
  const [tierLabels, setTierLabels] = useState({})
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [deleteTarget, setDeleteTarget] = useState(null)
  const [deleting, setDeleting] = useState(false)

  /** 0-based: collapse all branches beyond this indent (0 = only roots expanded if defaultOpen computed) */
  const [expandLevels, setExpandLevels] = useState(0)
  const [listTab, setListTab] = useState('containers')
  const [referenceOverview, setReferenceOverview] = useState(null)
  const [capacityHintsByTier, setCapacityHintsByTier] = useState({})
  const [prefixProducts, setPrefixProducts] = useState({})
  const [viewMode, setViewMode] = useState('table')
  const [roleFilter, setRoleFilter] = useState('all')
  const canManageNestingRules = useMemo(() => getAuthPermissionSet().has('ManageNestingRules'), [])

  const fetchRules = useCallback(async () => {
    setLoading(true)
    try {
      const res = await superagent.get(`${backendUrl}/nesting-rules`).set(authHeader())
      const body = res.body || {}
      if (Array.isArray(body)) {
        setRules(body)
        setEffectiveSource('database')
        setReferenceOverview(null)
        setTierLabels({})
        setCapacityHintsByTier({})
      } else {
        setRules(body.rules || [])
        setEffectiveSource(body.effectiveSource || 'database')
        setReferenceOverview(body.referenceOverview ?? null)
        setTierLabels(body.tierLabels || {})
        setCapacityHintsByTier(body.capacityHintsByTier || {})
      }
    } catch (e) {
      console.error('Failed to fetch nesting rules', e)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { fetchRules() }, [fetchRules])

  const fetchPrefixProducts = useCallback(async () => {
    try {
      const res = await superagent.get(`${backendUrl}/nesting-rules/prefix-products`).set(authHeader())
      setPrefixProducts(res.body || {})
    } catch (e) {
      console.error('Failed to fetch prefix products', e)
      setPrefixProducts({})
    }
  }, [])

  useEffect(() => { fetchPrefixProducts() }, [fetchPrefixProducts])

  /** Full prefix map for tree edges (tier/search only affect which roots/table rows appear). */
  const ruleByPrefix = useMemo(() => {
    const m = new Map()
    for (const r of rules) {
      if (r.prefix) m.set(r.prefix, r)
    }
    return m
  }, [rules])

  const tierOptions = useMemo(() => {
    const entries = Object.entries(tierLabels || {})
    if (entries.length) return [...entries].sort(([a], [b]) => Number(a) - Number(b)).map(([tier]) => [tier, getTierLabel(tierLabels, tier)])
    const tiers = new Set(rules.map((r) => r.tier).filter((tier) => tier != null && tier !== '').map(String))
    if (form.tier) tiers.add(String(form.tier))
    return [...tiers].sort((a, b) => Number(a) - Number(b)).map((tier) => [tier, getTierLabel(tierLabels, tier)])
  }, [tierLabels, rules, form.tier])

  const prefixOptions = useMemo(() => {
    return [
      { value: PREFIX_FILTER_ALL, label: 'All Prefixes' },
      ...sortPrefixes([...new Set(rules.map((r) => r.prefix))]).map((prefix) => ({
        value: prefix,
        label: prefix,
      })),
    ]
  }, [rules])

  const selectedPrefixOption = useMemo(() => {
    if (!search) return prefixOptions[0] || null
    return prefixOptions.find((option) => option.value === search) || null
  }, [prefixOptions, search])

  const tierFilterOptions = useMemo(() => {
    return [
      { value: 'all', label: 'All Tiers' },
      ...tierOptions.map(([value, label]) => ({ value, label })),
    ]
  }, [tierOptions])

  const selectedTierFilterOption = useMemo(() => {
    return tierFilterOptions.find((option) => option.value === tierFilter) || tierFilterOptions[0]
  }, [tierFilterOptions, tierFilter])

  const formTierOptions = useMemo(() => {
    return tierOptions.map(([value, label]) => ({ value, label }))
  }, [tierOptions])

  const selectedFormTierOption = useMemo(() => {
    return formTierOptions.find((option) => String(option.value) === String(form.tier)) || null
  }, [formTierOptions, form.tier])

  const expandOptions = useMemo(() => ([
    { value: '1', label: '1 level' },
    { value: '2', label: '2 levels' },
    { value: '3', label: '3 levels' },
    { value: '4', label: '4 levels' },
    { value: '8', label: '8 levels' },
    { value: '0', label: 'Collapsed' },
    { value: String(MAX_TREE_DEPTH), label: 'All levels' },
  ]), [])

  const selectedExpandOption = useMemo(() => {
    return expandOptions.find((option) => option.value === String(expandLevels)) || null
  }, [expandLevels, expandOptions])

  const applyListFilters = useCallback((list) => {
    let out = list
    if (tierFilter !== 'all') out = out.filter((r) => String(r.tier) === tierFilter)
    if (search) {
      const s = search.toUpperCase()
      out = out.filter((r) => r.prefix?.toUpperCase() === s)
    }
    return out
  }, [tierFilter, search])

  const searchTerm = search.trim().toUpperCase()

  const allContainedPrefixes = useMemo(() => {
    const s = new Set()
    for (const r of rules) {
      if (r.flags?.isAccessory) continue
      for (const c of (r.nestingRelationships || [])) s.add(c)
    }
    return s
  }, [rules])

  const prefixHasSearchMatch = useCallback(function hasMatch (prefix, visited = new Set()) {
    if (!searchTerm) return true
    if (String(prefix || '').toUpperCase().includes(searchTerm)) return true
    if (visited.has(prefix)) return false

    visited.add(prefix)
    const rule = ruleByPrefix.get(prefix)
    return (rule?.nestingRelationships || []).some((childPrefix) => hasMatch(childPrefix, visited))
  }, [ruleByPrefix, searchTerm])

  const prefixHasTierMatch = useCallback(function hasMatch (prefix, visited = new Set()) {
    if (tierFilter === 'all') return true
    if (visited.has(prefix)) return false

    visited.add(prefix)
    const rule = ruleByPrefix.get(prefix)
    if (String(rule?.tier) === tierFilter) return true
    return (rule?.nestingRelationships || []).some((childPrefix) => hasMatch(childPrefix, visited))
  }, [ruleByPrefix, tierFilter])

  const parentPrefixesByChild = useMemo(() => {
    const parents = new Map()
    for (const rule of rules) {
      if (rule.flags?.isAccessory) continue
      for (const childPrefix of rule.nestingRelationships || []) {
        if (!childPrefix) continue
        if (!parents.has(childPrefix)) parents.set(childPrefix, new Set())
        parents.get(childPrefix).add(rule.prefix)
      }
    }
    return parents
  }, [rules])

  const { containerTreeRoots, ancestorParentByPrefix, supplementaryRootPrefixes } = useMemo(() => {
    const sortByHierarchy = (a, b) => compareRulesBySizeOrder(a, b)

    const buildAncestorGraphFromRoots = (roots) => {
      const parent = new Map()
      const visitChildren = (rootPrefix, visited = new Set()) => {
        if (visited.has(rootPrefix)) return
        visited.add(rootPrefix)
        const parentRule = ruleByPrefix.get(rootPrefix)
        for (const childPrefix of parentRule?.nestingRelationships || []) {
          if (!childPrefix) continue
          if (!parent.has(childPrefix)) {
            parent.set(childPrefix, rootPrefix)
            visitChildren(childPrefix, new Set(visited))
          }
        }
      }
      for (const root of roots) {
        parent.set(root.prefix, null)
      }
      for (const root of roots) {
        visitChildren(root.prefix)
      }
      return parent
    }

    const collectAncestorRules = (prefix, visited = new Set()) => {
      if (!prefix || visited.has(prefix)) return []
      visited.add(prefix)

      const result = []
      const parents = parentPrefixesByChild.get(prefix)
      if (!parents) return result

      for (const parentPrefix of parents) {
        const parentRule = ruleByPrefix.get(parentPrefix)
        if (!parentRule || parentRule.flags?.isAccessory) continue
        result.push(...collectAncestorRules(parentPrefix, new Set(visited)))
        result.push(parentRule)
      }
      return result
    }

    const base = rules.filter((r) => !r.flags?.isAccessory)

    const passesSearchAndTier = (r) => {
      if (searchTerm && !prefixHasSearchMatch(r.prefix)) return false
      if (tierFilter !== 'all' && !prefixHasTierMatch(r.prefix)) return false
      return true
    }

    const passesTier = (r) => {
      if (tierFilter !== 'all' && !prefixHasTierMatch(r.prefix)) return false
      return true
    }

    if (searchTerm) {
      const selectedRule = base.find((r) => String(r.prefix || '').toUpperCase() === searchTerm)
      if (!selectedRule || !passesSearchAndTier(selectedRule)) {
        return {
          containerTreeRoots: [],
          ancestorParentByPrefix: new Map(),
          supplementaryRootPrefixes: EMPTY_SUPPLEMENTARY_ROOTS,
        }
      }

      const selectedPrefixSet = new Set([selectedRule.prefix])
      const descendantClosure = new Set()
      const queue = [...(selectedRule.nestingRelationships || [])]
      while (queue.length) {
        const prefix = queue.shift()
        if (!prefix || descendantClosure.has(prefix)) continue
        descendantClosure.add(prefix)
        const row = ruleByPrefix.get(prefix)
        for (const childPrefix of row?.nestingRelationships || []) {
          if (childPrefix && !descendantClosure.has(childPrefix)) queue.push(childPrefix)
        }
      }

      const supplementaryByPrefix = new Map()
      for (const childPrefix of selectedRule.nestingRelationships || []) {
        if (!childPrefix || selectedPrefixSet.has(childPrefix)) continue
        const row = ruleByPrefix.get(childPrefix)
        if (!row || row.flags?.isAccessory || !passesTier(row)) continue
        supplementaryByPrefix.set(row.prefix, row)
      }
      for (const prefix of descendantClosure) {
        if (!prefix || selectedPrefixSet.has(prefix)) continue
        const row = ruleByPrefix.get(prefix)
        if (!row || row.flags?.isAccessory || !passesTier(row)) continue
        const tier = Number(row.tier)
        if (!Number.isFinite(tier) || tier < SEARCH_SUPPLEMENTARY_MIN_TIER) continue
        supplementaryByPrefix.set(row.prefix, row)
      }

      const supplementaryRoots = [...supplementaryByPrefix.values()].sort(sortByHierarchy)
      const ancestorRoots = collectAncestorRules(selectedRule.prefix)
      const combinedRootsByPrefix = new Map()
      for (const row of [...ancestorRoots, selectedRule, ...supplementaryRoots]) {
        if (!combinedRootsByPrefix.has(row.prefix)) combinedRootsByPrefix.set(row.prefix, row)
      }
      const combinedRoots = [...combinedRootsByPrefix.values()].sort(sortByHierarchy)

      return {
        containerTreeRoots: combinedRoots,
        ancestorParentByPrefix: buildAncestorGraphFromRoots(combinedRoots),
        supplementaryRootPrefixes: new Set(supplementaryRoots.map((r) => r.prefix)),
      }
    }

    const rootRowClause = (r) => {
      if (tierFilter === 'all') return true

      const t = Number(r.tier)
      const isContained = allContainedPrefixes.has(r.prefix)
      const isContainerRoot = t >= 1 && t <= CONTAINER_TREE_ROOT_MAX_TIER && !isContained
      const isNestedContainer =
        t >= 1 && t <= CONTAINER_TREE_ROOT_MAX_TIER && isContained && (r.nestingRelationships || []).length > 0
      const isDirectSearchMatch = !!searchTerm && String(r.prefix || '').toUpperCase().includes(searchTerm)
      const isDirectTierMatch = tierFilter !== 'all' && String(r.tier) === tierFilter

      return (
        isContainerRoot ||
        isNestedContainer ||
        isDirectSearchMatch ||
        isDirectTierMatch
      )
    }

    const filtered = base.filter(passesSearchAndTier).filter(rootRowClause)
    const sorted = [...filtered].sort(sortByHierarchy)
    return {
      containerTreeRoots: sorted,
      ancestorParentByPrefix: buildAncestorGraphFromRoots(sorted),
      supplementaryRootPrefixes: EMPTY_SUPPLEMENTARY_ROOTS,
    }
  }, [
    rules,
    tierFilter,
    searchTerm,
    prefixHasSearchMatch,
    prefixHasTierMatch,
    allContainedPrefixes,
    ruleByPrefix,
    parentPrefixesByChild,
  ])

  const accessoryTableRows = useMemo(() => {
    const base = rules.filter((r) => !!r.flags?.isAccessory)
    const filtered = applyListFilters(base)
    return [...filtered].sort((a, b) => String(a.prefix).localeCompare(String(b.prefix)))
  }, [rules, applyListFilters])

  // Primary "All rules" surface: every rule on one row, filtered by search/tier/role.
  const filteredRules = useMemo(() => {
    let out = applyListFilters(rules)
    if (roleFilter === 'container') out = out.filter((r) => !r.flags?.isAccessory)
    else if (roleFilter === 'accessory') out = out.filter((r) => !!r.flags?.isAccessory)
    return [...out].sort(compareRulesBySizeOrder)
  }, [rules, applyListFilters, roleFilter])

  const roleFilterOptions = useMemo(() => ([
    { value: 'all', label: 'All roles' },
    { value: 'container', label: 'Containers' },
    { value: 'accessory', label: 'Accessories' },
  ]), [])

  const selectedRoleFilterOption = useMemo(
    () => roleFilterOptions.find((o) => o.value === roleFilter) || roleFilterOptions[0],
    [roleFilterOptions, roleFilter],
  )

  const containerTierGroups = useMemo(() => {
    const byTier = new Map()
    for (const rule of containerTreeRoots) {
      const tierKey = rule?.tier != null && rule.tier !== '' ? String(rule.tier) : 'unknown'
      if (!byTier.has(tierKey)) {
        byTier.set(tierKey, {
          tier: rule?.tier,
          label: rule?.tier != null && rule.tier !== '' ? getTierLabel(tierLabels, rule.tier) : 'Unknown tier',
          rules: [],
        })
      }
      byTier.get(tierKey).rules.push(rule)
    }
    return [...byTier.values()].sort((a, b) => {
      const at = Number(a.tier)
      const bt = Number(b.tier)
      const aRank = Number.isFinite(at) ? at : 999
      const bRank = Number.isFinite(bt) ? bt : 999
      return aRank - bRank || String(a.label).localeCompare(String(b.label))
    })
  }, [containerTreeRoots, tierLabels])

  const specialRuleOverview = useMemo(() => {
    const restrictedRows = rules
      .filter((r) => r.nestingRestrictions?.length)
      .map((r) => ({
        prefix: r.prefix,
        prefixes: sortPrefixes(r.nestingRestrictions),
      }))
      .sort((a, b) => String(a.prefix).localeCompare(String(b.prefix)))

    const accessoriesOnlyContainers = sortPrefixes(
      rules.filter((r) => r.flags?.accessoriesOnlyContainer).map((r) => r.prefix),
    )
    const cannotBeContainedPrefixes = sortPrefixes(
      rules.filter((r) => r.flags?.cannotBeContained).map((r) => r.prefix),
    )
    const cannotBeNestedPrefixes = sortPrefixes(
      rules.filter((r) => r.flags?.cannotBeNested).map((r) => r.prefix),
    )
    const hierarchyGroups = [...rules]
      .sort(compareRulesBySizeOrder)
      .reduce((groups, rule) => {
        const tierKey = rule?.tier != null && rule.tier !== '' ? String(rule.tier) : 'unknown'
        if (!groups.has(tierKey)) {
          groups.set(tierKey, {
            tier: rule?.tier,
            label: rule?.tier != null && rule.tier !== '' ? getTierLabel(tierLabels, rule.tier) : 'Unknown tier',
            rules: [],
          })
        }
        groups.get(tierKey).rules.push(rule)
        return groups
      }, new Map())

    return {
      restrictedRows,
      accessoriesOnlyContainers,
      cannotBeContainedPrefixes,
      cannotBeNestedPrefixes,
      hierarchyGroups: [...hierarchyGroups.values()]
        .map((group) => ({
          ...group,
          rules: [...group.rules].sort(compareRulesBySizeOrder),
        }))
        .sort((a, b) => {
          const at = Number(a.tier)
          const bt = Number(b.tier)
          const aRank = Number.isFinite(at) ? at : 999
          const bRank = Number.isFinite(bt) ? bt : 999
          return aRank - bRank || String(a.label).localeCompare(String(b.label))
        }),
    }
  }, [rules, tierLabels])

  const sourceReferenceOverview = useMemo(() => {
    if (!referenceOverview) return null
    return {
      restrictedRows: (referenceOverview.nestingRestrictions || []).map((row) => ({
        prefix: row.restrictedPrefix,
        prefixes: sortPrefixes(row.cannotBePackedIntoPrefixes),
      })),
      accessoriesOnlyContainers: sortPrefixes(referenceOverview.accessoriesOnlyContainers),
      cannotBeContainedPrefixes: sortPrefixes(referenceOverview.cannotBePackedInsideAnyContainerPrefixes),
      cannotBeNestedPrefixes: sortPrefixes(referenceOverview.cannotBeNestedPrefixes),
      hierarchyGroups: [],
    }
  }, [referenceOverview])

  const specialOverview = specialRuleOverview.restrictedRows.length ||
    specialRuleOverview.accessoriesOnlyContainers.length ||
    specialRuleOverview.cannotBeContainedPrefixes.length ||
    specialRuleOverview.cannotBeNestedPrefixes.length
    ? specialRuleOverview
    : sourceReferenceOverview

  const setField = (key, value) => setForm((prev) => ({ ...prev, [key]: value }))
  const setSizeOrderField = (value) => {
    setField('sizeOrder', String(value).replace(/\D/g, ''))
  }

  const openCreate = () => {
    if (!canManageNestingRules) return
    if (effectiveSource === 'constants') {
      toast.error('Turn on enable_nesting_config before creating editable database rules.')
      return
    }
    setEditId(null)
    setForm({ ...EMPTY_FORM })
    setDialogOpen(true)
  }

  const openEdit = (ruleRow) => {
    if (!canManageNestingRules) return
    if (!ruleRow) return
    if (effectiveSource === 'constants' || ruleRow.readonlyFromConstants) {
      toast.error('BE automation source rules are read-only. Turn on enable_nesting_config and use database rules to edit.')
      return
    }
    setEditId(ruleRow._id)
    setForm({
      prefix: ruleRow.prefix || '',
      tier: String(ruleRow.tier ?? 5),
      nestingRelationships: ruleRow.nestingRelationships || [],
      accessoryNesting: ruleRow.accessoryNesting || [],
      nestingRestrictions: ruleRow.nestingRestrictions || [],
      'flags.cannotBeContained': !!ruleRow.flags?.cannotBeContained,
      'flags.accessoriesOnlyContainer': !!ruleRow.flags?.accessoriesOnlyContainer,
      'flags.isAccessory': !!ruleRow.flags?.isAccessory,
      'flags.cannotBeNested': !!ruleRow.flags?.cannotBeNested,
      sizeOrder: ruleRow.sizeOrder ?? '',
      active: ruleRow.active !== false,
    })
    setDialogOpen(true)
  }

  const parseNum = (val) => (val === '' || val === undefined) ? undefined : Number(val)
  const asArray = (val) => Array.isArray(val) ? val.filter(Boolean) : []

  // One-fact-one-owner: the UI only exposes the relationship field this rule owns
  // (container -> nestingRelationships, accessory -> accessoryNesting). The other field
  // round-trips untouched from openEdit, so asymmetric accessoryNesting (e.g. STR->CME)
  // is never blanked. The stored contract (all three arrays) is unchanged.
  const buildPayload = () => ({
    prefix: form.prefix.trim().toUpperCase(),
    tier: Number(form.tier),
    nestingRelationships: asArray(form.nestingRelationships),
    accessoryNesting: asArray(form.accessoryNesting),
    nestingRestrictions: asArray(form.nestingRestrictions),
    flags: {
      cannotBeContained: form['flags.cannotBeContained'],
      accessoriesOnlyContainer: form['flags.accessoriesOnlyContainer'],
      isAccessory: form['flags.isAccessory'],
      cannotBeNested: form['flags.cannotBeNested'],
    },
    sizeOrder: parseNum(form.sizeOrder) ?? 999,
    active: form.active,
  })

  const handleSave = async () => {
    if (!canManageNestingRules) return
    const payload = buildPayload()
    if (!payload.prefix) return toast.error('Prefix is required')
    if (!Number.isFinite(Number(payload.sizeOrder)) || Number(payload.sizeOrder) < 1) {
      return toast.error('Size order must be 1 or greater')
    }
    setSaving(true)
    try {
      if (editId) {
        await superagent.put(`${backendUrl}/nesting-rules/${editId}`).set(authHeader()).send(payload)
      } else {
        await superagent.post(`${backendUrl}/nesting-rules`).set(authHeader()).send(payload)
      }
      setDialogOpen(false)
      fetchRules()
    } catch (e) {
      console.error(e)
      toast.error(e?.response?.body?.message || 'Save failed')
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = (id) => {
    if (!canManageNestingRules) return
    if (!id) return
    const rule = rules.find((r) => r._id === id)
    setDeleteTarget(rule || { _id: id })
    setDeleteDialogOpen(true)
  }

  const confirmDelete = async () => {
    if (!canManageNestingRules) return
    if (!deleteTarget?._id) return
    setDeleting(true)
    try {
      await superagent.delete(`${backendUrl}/nesting-rules/${deleteTarget._id}`).set(authHeader())
      setDeleteDialogOpen(false)
      setDeleteTarget(null)
      fetchRules()
    } catch (e) {
      console.error(e)
      toast.error(e?.response?.body?.message || 'Delete failed')
    } finally {
      setDeleting(false)
    }
  }

  const handleSeed = async () => {
    if (!canManageNestingRules) return
    setSeedDialogOpen(true)
  }

  const confirmSeed = async () => {
    if (!canManageNestingRules) return
    setSeeding(true)
    try {
      const res = await superagent.post(`${backendUrl}/nesting-rules/seed`).set(authHeader())
      toast.success(formatSeedToast(res.body), { autoClose: 10000 })
      setSeedDialogOpen(false)
      fetchRules()
    } catch (e) {
      console.error(e)
      toast.error(e?.response?.body?.message || 'Seed failed')
    } finally {
      setSeeding(false)
    }
  }

  const specialEditable = canManageNestingRules && effectiveSource === 'database'
  const editByPrefix = (prefix) => {
    const rule = ruleByPrefix.get(prefix)
    if (rule) openEdit(rule)
  }

  return (
    <div className="page-content">
      <ToastContainer position="top-right" autoClose={3000} hideProgressBar />
      <div className="w-full px-3 mx-auto max-w-5xl xl:max-w-6xl pt-4">
        <div className="mb-4 flex flex-wrap items-center gap-2 text-sm">
          <span className="font-bold uppercase text-gray-700">Nesting Rules</span>
          <span className="text-gray-500">-</span>
          <strong
            className={[
              'rounded-md border px-3 py-2',
              effectiveSource === 'database'
                ? 'border-emerald-200 bg-emerald-50 text-emerald-900'
                : 'border-amber-200 bg-amber-50 text-amber-900',
            ].join(' ')}
          >
            {effectiveSource === 'database' ? 'Live source: Database (editable)' : 'Live source: Built-in defaults (read-only)'}
          </strong>
          <span className="text-xs text-gray-500">
            {effectiveSource === 'database'
              ? 'Edits here drive the packing engine.'
              : 'Turn on the enable_nesting_config feature flag to edit rules here.'}
          </span>
        </div>

        <div className="nesting-rule-toolbar mb-4">
          <div className="nesting-rule-search">
            <label className="block text-xs mb-1 text-black">Prefix</label>
            <ReactSelect
              value={selectedPrefixOption}
              onChange={(option) => setSearch(!option || option.value === PREFIX_FILTER_ALL ? '' : option.value)}
              options={prefixOptions}
              isClearable={false}
              isSearchable
              placeholder="Select prefix"
              noOptionsMessage={() => loading ? 'Loading prefixes...' : 'No prefix found'}
              styles={{
                menuPortal: (base) => ({ ...base, zIndex: 9999 }),
              }}
              menuPortalTarget={document.body}
            />
          </div>
          <div className="nesting-rule-filter">
            <label className="block text-xs mb-1 text-black">Tier</label>
            <ReactSelect
              value={selectedTierFilterOption}
              onChange={(option) => setTierFilter(option?.value || 'all')}
              options={tierFilterOptions}
              isSearchable
              isClearable={false}
              placeholder="All Tiers"
              menuPortalTarget={document.body}
            />
          </div>
          <div className="nesting-rule-filter">
            <label className="block text-xs mb-1 text-black">Role</label>
            <ReactSelect
              value={selectedRoleFilterOption}
              onChange={(option) => setRoleFilter(option?.value || 'all')}
              options={roleFilterOptions}
              isSearchable={false}
              isClearable={false}
              menuPortalTarget={document.body}
            />
          </div>
          <div className="nesting-rule-filter">
            <label className="block text-xs mb-1 text-black whitespace-nowrap">Initial expand</label>
            <ReactSelect
              value={selectedExpandOption}
              onChange={(option) => setExpandLevels(Number(option?.value || 0))}
              options={expandOptions}
              isSearchable={false}
              isClearable={false}
              menuPortalTarget={document.body}
            />
          </div>
          <div className="nesting-rule-toolbar-actions">
            <div className="flex overflow-hidden rounded-md border border-gray-300">
              <Button
                type="button"
                variant={viewMode === 'table' ? 'default' : 'ghost'}
                size="sm"
                className="rounded-none"
                onClick={() => setViewMode('table')}
              >
                Table
              </Button>
              <Button
                type="button"
                variant={viewMode === 'hierarchy' ? 'default' : 'ghost'}
                size="sm"
                className="rounded-none"
                onClick={() => setViewMode('hierarchy')}
              >
                Hierarchy
              </Button>
            </div>
            {canManageNestingRules ? (
              <>
                <Button variant="outline" title="Copy the backend built-in default rules into the editable database set" onClick={handleSeed} disabled={seeding}>{seeding ? 'Seeding...' : 'Seed defaults'}</Button>
                <Button onClick={openCreate} disabled={effectiveSource === 'constants'} title={effectiveSource === 'constants' ? 'Turn on enable_nesting_config first' : ''}>New rule</Button>
              </>
            ) : null}
            <Button variant="outline" onClick={() => { fetchRules(); fetchPrefixProducts() }}>Refresh</Button>
          </div>
        </div>

        {viewMode === 'table' ? (
          <Card className="mb-6 pt-3">
            <CardBody>
              <div className="mb-3 text-xs text-gray-600">Every rule, fully editable here. Search by product or prefix; filter by tier and role. Switch to <strong>Hierarchy</strong> to see the packing tree.</div>
              <div className="min-h-[200px] overflow-x-auto rounded-lg border border-gray-200 bg-white text-black">
                {loading ? (
                  <div className="p-12 text-center text-gray-500">Loading…</div>
                ) : filteredRules.length === 0 ? (
                  <div className="p-12 text-center text-gray-600">No rules match this filter.</div>
                ) : (
                  <table className="w-full min-w-[860px] border-collapse text-left text-xs text-black">
                    <thead>
                      <tr className="border-b border-gray-200 bg-slate-50 text-[11px] text-gray-700">
                        <th className="px-3 py-2 font-semibold">Prefix</th>
                        <th className="px-3 py-2 font-semibold">Products</th>
                        <th className="px-3 py-2 font-semibold">Tier</th>
                        <th className="px-3 py-2 font-semibold">Role</th>
                        <th className="px-3 py-2 font-semibold">Packs inside / into</th>
                        <th className="px-3 py-2 font-semibold">Cannot pack into</th>
                        <th className="px-3 py-2 font-semibold">Flags</th>
                        <th className="px-3 py-2 font-semibold">Active</th>
                        <th className="w-[1%] whitespace-nowrap px-3 py-2 font-semibold text-right"> </th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredRules.map((r) => {
                        const canMutate = canManageNestingRules && effectiveSource === 'database' && !r.readonlyFromConstants && r._id
                        const isAccessory = !!r.flags?.isAccessory
                        const owned = isAccessory ? (r.accessoryNesting || []) : (r.nestingRelationships || [])
                        const flagBadges = [
                          r.flags?.cannotBeContained && 'cannot be contained',
                          r.flags?.accessoriesOnlyContainer && 'accessories-only',
                          r.flags?.cannotBeNested && 'cannot be nested',
                        ].filter(Boolean)
                        const friendly = derivedLabelFor(r.prefix, prefixProducts)
                        return (
                          <tr key={r.prefix} className={['border-b border-gray-100 hover:bg-gray-50', r.active === false ? 'bg-gray-50 text-gray-500' : ''].join(' ')}>
                            <td className="px-3 py-2">
                              <div className="flex min-w-0 flex-col">
                                <span className="font-mono text-sm font-semibold">{r.prefix}</span>
                                {friendly ? <span className="text-[11px] text-gray-600">{friendly}</span> : null}
                              </div>
                            </td>
                            <td className="px-3 py-2 tabular-nums">{productCount(r.prefix, prefixProducts) || '—'}</td>
                            <td className="px-3 py-2"><TierBadgeOnly tier={r.tier} tierLabels={tierLabels} /></td>
                            <td className="px-3 py-2">{isAccessory ? 'Accessory' : 'Container'}</td>
                            <td className="max-w-[16rem] px-3 py-2 font-mono text-[11px] text-gray-800">{owned.length ? owned.join(', ') : '—'}</td>
                            <td className="max-w-[12rem] px-3 py-2 font-mono text-[11px] text-gray-800">{(r.nestingRestrictions || []).length ? r.nestingRestrictions.join(', ') : '—'}</td>
                            <td className="px-3 py-2">
                              {flagBadges.length ? (
                                <div className="flex flex-wrap gap-1">
                                  {flagBadges.map((f) => <Badge key={f} variant="outline" className="text-[10px]">{f}</Badge>)}
                                </div>
                              ) : '—'}
                            </td>
                            <td className="px-3 py-2">{r.active === false ? <span className="rounded bg-gray-200 px-1.5 py-0.5 text-[10px]">inactive</span> : 'active'}</td>
                            <td className="px-3 py-2 text-right">
                              {canMutate ? (
                                <div className="flex justify-end gap-1">
                                  <Button type="button" variant="outline" size="sm" className="h-7 text-xs" onClick={() => openEdit(r)}>Edit</Button>
                                  <Button type="button" variant="destructive" size="sm" className="h-7 text-xs" onClick={() => handleDelete(r._id)}>Delete</Button>
                                </div>
                              ) : null}
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                )}
              </div>
            </CardBody>
          </Card>
        ) : (
        <Card className="mb-6 pt-3">
          <CardBody>
            <Tabs value={listTab} onValueChange={setListTab} className="w-full">
              <TabsList className="mb-4 flex h-auto flex-wrap justify-start gap-1">
                <TabsTrigger value="containers" className="text-sm">
                  Containers ({containerTreeRoots.length})
                </TabsTrigger>
                <TabsTrigger value="accessories" className="text-sm">
                  Accessories ({accessoryTableRows.length})
                </TabsTrigger>
                <TabsTrigger value="special" className="text-sm">
                  Special / Restricted
                </TabsTrigger>
              </TabsList>

              <TabsContent value="containers" className="mt-0 outline-none">
                <div className="mb-3 text-xs text-gray-600">
                  <p>
                    Container prefixes are shown once; parent rows show direct children from <code className="text-[10px]">NESTING_RELATIONSHIPS</code>.
                  </p>
                </div>
                <div className="mb-3 flex flex-wrap items-center justify-between gap-2 text-xs text-gray-500">
                  <div className="flex flex-wrap items-center gap-3">
                    <Button
                      type="button"
                      variant="success"
                      size="sm"
                      className="h-8 rounded-md px-4 text-xs font-semibold text-white"
                      style={{ backgroundColor: '#34c38f', borderColor: '#34c38f', color: '#fff' }}
                      onClick={() => setExpandLevels(expandLevels >= MAX_TREE_DEPTH ? 0 : MAX_TREE_DEPTH)}
                    >
                      {expandLevels >= MAX_TREE_DEPTH ? 'Close All' : 'Expand All'}
                    </Button>
                    <span>
                      Expand up to depth{' '}
                      {expandLevels >= MAX_TREE_DEPTH ? 'all' : expandLevels}
                    </span>
                  </div>
                </div>
                <div className="min-h-[200px] rounded-lg border border-gray-200 bg-white text-black">
                  {loading ? (
                    <div className="p-12 text-center text-gray-500">Loading…</div>
                  ) : containerTreeRoots.length === 0 ? (
                    <div className="p-12 text-center text-gray-600">No container roots match this filter (try tier &quot;all&quot; or clear search).</div>
                  ) : (
                    <div className="divide-y divide-gray-200">
                      {containerTierGroups.map((group) => {
                        const tierStyle = getTierStyle(group.tier)
                        return (
                        <section
                          key={`tier-group-${group.label}`}
                          className="bg-white"
                          style={{ borderLeft: `4px solid ${tierStyle.border}` }}
                        >
                          <div
                            className="flex items-center gap-2 border-b border-gray-100 px-3 py-2"
                            style={{ backgroundColor: tierStyle.panel }}
                          >
                            <CapacityHintTooltip tier={group.tier} capacityHintsByTier={capacityHintsByTier} tierLabels={tierLabels}>
                              <Badge
                                variant="outline"
                                className="max-w-full cursor-help truncate text-[11px]"
                                style={getTierBadgeStyle(group.tier)}
                              >
                                {group.label}
                              </Badge>
                            </CapacityHintTooltip>
                            <Badge variant="secondary" className="whitespace-nowrap font-mono text-[11px] text-black tabular-nums">
                              {group.rules.length}
                              {' '}
                              {group.rules.length === 1 ? 'prefix' : 'prefixes'}
                            </Badge>
                          </div>
                          <div className="divide-y divide-gray-100">
                            {group.rules.map((r) => (
                              <PrefixTreeBranch
                                key={`${expandLevels}-${r.prefix}${supplementaryRootPrefixes.has(r.prefix) ? '-surface' : ''}`}
                                rule={r}
                                depth={0}
                                ancestorPrefixes={[]}
                                ruleByPrefix={ruleByPrefix}
                                effectiveSource={effectiveSource}
                                canManageNestingRules={canManageNestingRules}
                                openEdit={openEdit}
                                handleDelete={handleDelete}
                                defaultExpandedDepth={expandLevels}
                                tierLabels={tierLabels}
                                searchTerm={searchTerm}
                                prefixHasSearchMatch={prefixHasSearchMatch}
                                tierFilter={tierFilter}
                                prefixHasTierMatch={prefixHasTierMatch}
                                ancestorParentByPrefix={ancestorParentByPrefix}
                                supplementaryRootPrefixes={supplementaryRootPrefixes}
                                prefixProducts={prefixProducts}
                                treeRootKind={supplementaryRootPrefixes.has(r.prefix) ? 'supplementary' : 'anchor'}
                                showTier={false}
                              />
                            ))}
                          </div>
                        </section>
                        )
                      })}
                    </div>
                  )}
                </div>
              </TabsContent>

              <TabsContent value="accessories" className="mt-0 outline-none">
                <p className="mb-3 text-xs text-gray-600">
                  <strong> Accessories are packed into</strong> outer containers, column label is{' '}
                  <strong>{LABEL_CAN_PACK_INTO}</strong> (not &quot;can contain&quot;).
                </p>
                <div className="min-h-[200px] overflow-x-auto rounded-lg border border-gray-200 bg-white text-black">
                  {loading ? (
                    <div className="p-12 text-center text-gray-500">Loading…</div>
                  ) : accessoryTableRows.length === 0 ? (
                    <div className="p-12 text-center text-gray-600">No accessory rules match this filter.</div>
                  ) : (
                    <table className="w-full min-w-[640px] border-collapse text-left text-xs text-black">
                      <thead>
                        <tr className="border-b border-gray-200 bg-slate-50 text-[11px] text-gray-700">
                          <th className="whitespace-nowrap px-3 py-2 font-semibold">Accessory prefix</th>
                          <th className="whitespace-nowrap px-3 py-2 font-semibold">Tier</th>
                          <th className="min-w-0 px-3 py-2 font-semibold">{LABEL_CAN_PACK_INTO.replace(':', '')}</th>
                          <th className="w-[1%] whitespace-nowrap px-3 py-2 font-semibold text-right"> </th>
                        </tr>
                      </thead>
                      <tbody>
                        {accessoryTableRows.map((r) => {
                          const into = sortRelationshipPrefixes(r.accessoryNesting || [], ruleByPrefix)
                          const isInactive = r.active === false
                          const canMutate =
                            canManageNestingRules && effectiveSource === 'database' && !r.readonlyFromConstants && r._id
                          return (
                            <tr
                              key={r.prefix}
                              className={[
                                'border-b border-gray-100 hover:bg-gray-50',
                                isInactive ? 'bg-gray-50 text-gray-500' : '',
                              ].join(' ')}
                            >
                              <td className="max-w-[14rem] px-3 py-2" title={r.prefix}>
                                <div className="flex min-w-0 flex-col">
                                  <div className="flex min-w-0 items-center gap-2">
                                    <span className={['truncate font-mono text-sm font-semibold', isInactive ? 'text-gray-500' : 'text-black'].join(' ')}>
                                      {r.prefix}
                                    </span>
                                    {isInactive ? (
                                      <span className="shrink-0 rounded bg-gray-200 px-1.5 py-0.5 text-[10px] font-medium text-gray-600">
                                        inactive
                                      </span>
                                    ) : null}
                                  </div>
                                  {derivedLabelFor(r.prefix, prefixProducts) ? (
                                    <span className="truncate text-[11px] text-gray-600">{derivedLabelFor(r.prefix, prefixProducts)}</span>
                                  ) : null}
                                </div>
                              </td>
                              <td className="px-3 py-2">
                                {r.tier != null && r.tier !== '' ? (
                                  <Badge
                                    variant="outline"
                                    className="text-[11px]"
                                    style={getTierBadgeStyle(r.tier)}
                                  >
                                    {getTierLabel(tierLabels, r.tier)}
                                  </Badge>
                                ) : (
                                  <span className="text-gray-400">—</span>
                                )}
                              </td>
                              <td className="min-w-[200px] px-3 py-2 align-top">
                                {into.length ? (
                                  <PrefixWrapList
                                    prefixes={into}
                                    suffix={(
                                      <Badge variant="secondary" className="inline-flex h-5 items-center whitespace-nowrap font-mono text-[11px] leading-none text-black tabular-nums">
                                        {into.length}
                                        {' '}
                                        {into.length === 1 ? 'parent' : 'parents'}
                                      </Badge>
                                    )}
                                  />
                                ) : (
                                  <span className="text-gray-400">—</span>
                                )}
                              </td>
                              <td className="px-3 py-2 text-right">
                                {canMutate ? (
                                  <div className="flex shrink-0 justify-end gap-1">
                                    <Button type="button" variant="outline" size="sm" className="h-7 text-xs" onClick={() => openEdit(r)}>
                                      Edit
                                    </Button>
                                    <Button type="button" variant="destructive" size="sm" className="h-7 text-xs" onClick={() => handleDelete(r._id)}>
                                      Delete
                                    </Button>
                                  </div>
                                ) : null}
                              </td>
                            </tr>
                          )
                        })}
                      </tbody>
                    </table>
                  )}
                </div>
              </TabsContent>

              <TabsContent value="special" className="mt-0 outline-none">
                <p className="mb-4 text-xs text-gray-600">
                  Restrictions and special flags, derived from the current rule set.{' '}
                  {specialEditable ? 'Click any prefix to edit its rule.' : 'Enable database mode to edit these.'}
                </p>
                {!specialOverview ? (
                  <div className="rounded-md border border-gray-200 bg-slate-50 px-4 py-6 text-sm text-gray-600">
                    No special restrictions or flags are available for the current rule set.
                  </div>
                ) : (
                  <div className="space-y-8 text-xs text-black">
                    <section>
                      <h3 className="mb-2 text-sm font-semibold text-gray-900">Product size hierarchy</h3>
                      <p className="mb-2 text-gray-600">
                        Top to bottom: largest to smallest.
                      </p>
                      <div className="space-y-3">
                        {(specialOverview.hierarchyGroups || []).length === 0 ? (
                          <div className="rounded-lg border border-gray-200 bg-white px-3 py-4 text-gray-500">
                            No hierarchy rows available.
                          </div>
                        ) : specialOverview.hierarchyGroups.map((group) => {
                          const tierStyle = getTierStyle(group.tier)
                          return (
                            <section
                              key={`hierarchy-group-${group.label}`}
                              className="rounded-lg border bg-white"
                              style={{ borderColor: tierStyle.border }}
                            >
                              <div
                                className="flex flex-wrap items-center gap-2 border-b px-3 py-2"
                                style={{ backgroundColor: tierStyle.panel, borderColor: tierStyle.border }}
                              >
                                <Badge
                                  variant="outline"
                                  className="max-w-full truncate text-[12px] font-semibold"
                                  style={getTierBadgeStyle(group.tier)}
                                >
                                  {group.label}
                                </Badge>
                                <Badge variant="secondary" className="whitespace-nowrap font-mono text-[11px] text-black tabular-nums">
                                  {group.rules.length}
                                  {' '}
                                  {group.rules.length === 1 ? 'prefix' : 'prefixes'}
                                </Badge>
                              </div>
                              <div className="flex flex-wrap gap-2 p-3">
                                {group.rules.map((rule) => {
                                  const canMutate = canManageNestingRules && effectiveSource === 'database' && !rule.readonlyFromConstants && rule._id
                                  const content = (
                                    <>
                                      <span className="font-mono text-[10px] font-normal text-gray-500 tabular-nums">
                                        {getRuleSizeOrder(rule)}
                                      </span>
                                      <span className="font-mono text-sm font-semibold text-black">{rule.prefix}</span>
                                    </>
                                  )

                                  return canMutate ? (
                                    <button
                                      key={`hierarchy-${rule.prefix}`}
                                      type="button"
                                      className={[
                                        'inline-flex h-9 max-w-full items-center gap-2 rounded-md border px-3 text-left shadow-sm hover:border-gray-300',
                                        rule.active === false
                                          ? 'border-gray-200 bg-gray-100 text-gray-500 hover:bg-gray-100'
                                          : 'border-gray-200 bg-white hover:bg-gray-50',
                                      ].join(' ')}
                                      title={`Edit ${rule.prefix}`}
                                      onClick={() => openEdit(rule)}
                                    >
                                      {content}
                                      {rule.active === false ? (
                                        <span className="rounded bg-gray-200 px-1.5 py-0.5 text-[10px] font-medium text-gray-600">
                                          inactive
                                        </span>
                                      ) : null}
                                    </button>
                                  ) : (
                                    <div
                                      key={`hierarchy-${rule.prefix}`}
                                      className={[
                                        'inline-flex h-9 max-w-full items-center gap-2 rounded-md border border-gray-200 px-3 shadow-sm',
                                        rule.active === false ? 'bg-gray-100 text-gray-500' : 'bg-white',
                                      ].join(' ')}
                                      title={rule.prefix}
                                    >
                                      {content}
                                      {rule.active === false ? (
                                        <span className="rounded bg-gray-200 px-1.5 py-0.5 text-[10px] font-medium text-gray-600">
                                          inactive
                                        </span>
                                      ) : null}
                                    </div>
                                  )
                                })}
                              </div>
                            </section>
                          )
                        })}
                      </div>
                    </section>

                    <section>
                      <h3 className="mb-2 text-sm font-semibold text-gray-900">Blocked container pairs</h3>
                      <p className="mb-2 text-gray-600">Specific outer containers that this item cannot go into.</p>
                      <div className="overflow-x-auto rounded-lg border border-gray-200">
                        <table className="w-full min-w-[480px] border-collapse text-left">
                          <thead>
                            <tr className="border-b border-gray-200 bg-slate-50 text-[11px] text-gray-700">
                              <th className="px-3 py-2 font-semibold">Item prefix</th>
                              <th className="px-3 py-2 font-semibold">Blocked outer container prefixes</th>
                            </tr>
                          </thead>
                          <tbody>
                            {specialOverview.restrictedRows.length === 0 ? (
                              <tr>
                                <td colSpan={2} className="px-3 py-4 text-gray-500">None listed</td>
                              </tr>
                            ) : specialOverview.restrictedRows.map((row) => (
                              <tr key={row.prefix} className="border-b border-gray-100">
                                <td className="whitespace-nowrap px-3 py-2 font-mono text-sm font-semibold">
                                  {specialEditable && ruleByPrefix.get(row.prefix) ? (
                                    <button type="button" className="text-emerald-800 underline underline-offset-2 hover:text-emerald-900" onClick={() => editByPrefix(row.prefix)} title={`Edit ${row.prefix}`}>{row.prefix}</button>
                                  ) : row.prefix}
                                </td>
                                <td className="px-3 py-2 font-mono text-[11px] text-gray-800">{row.prefixes.join(', ')}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </section>

                    <section>
                      <h3 className="mb-2 text-sm font-semibold text-gray-900">Accessories-only containers</h3>
                      <p className="mb-2 text-gray-600">
                        These containers can hold accessories only, not normal luggage items.
                      </p>
                      <div className="flex flex-wrap gap-1.5 rounded-lg border border-gray-200 bg-white p-3">
                        {specialOverview.accessoriesOnlyContainers.length === 0 ? (
                          <span className="text-gray-500">None listed</span>
                        ) : specialOverview.accessoriesOnlyContainers.map((p) => (
                            <SpecialPrefixBadge key={p} prefix={p} variant="secondary" canEdit={specialEditable && !!ruleByPrefix.get(p)} onEdit={editByPrefix} />
                          ))}
                      </div>
                    </section>

                    <section>
                      <h3 className="mb-2 text-sm font-semibold text-gray-900">Cannot be contained</h3>
                      <p className="mb-2 text-gray-600">
                        Products that are too large or restricted from being nested inside any other container.
                      </p>
                      <div className="flex flex-wrap gap-1.5 rounded-lg border border-gray-200 bg-white p-3">
                        {specialOverview.cannotBeContainedPrefixes.length === 0 ? (
                          <span className="text-gray-500">None listed</span>
                        ) : (
                          specialOverview.cannotBeContainedPrefixes.map((p) => (
                            <SpecialPrefixBadge key={p} prefix={p} variant="outline" canEdit={specialEditable && !!ruleByPrefix.get(p)} onEdit={editByPrefix} />
                          ))
                        )}
                      </div>
                    </section>

                    <section>
                      <h3 className="mb-2 text-sm font-semibold text-gray-900">Cannot be nested</h3>
                      <p className="mb-2 text-gray-600">Items that cannot be nested.</p>
                      <div className="flex flex-wrap gap-1.5 rounded-lg border border-gray-200 bg-white p-3">
                        {specialOverview.cannotBeNestedPrefixes.length === 0 ? (
                          <span className="text-gray-500">None listed</span>
                        ) : (
                          specialOverview.cannotBeNestedPrefixes.map((p) => (
                            <SpecialPrefixBadge key={p} prefix={p} variant="outline" canEdit={specialEditable && !!ruleByPrefix.get(p)} onEdit={editByPrefix} />
                          ))
                        )}
                      </div>
                    </section>
                  </div>
                )}
              </TabsContent>
            </Tabs>
          </CardBody>
        </Card>
        )}

        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent
            className="nesting-rule-edit-dialog min-w-[60vw] gap-1 overflow-y-auto bg-white p-3 text-black"
          >
            <DialogHeader>
              <DialogTitle>{editId ? 'Edit Nesting Rule' : 'New Nesting Rule'}</DialogTitle>
            </DialogHeader>
            <div className="space-y-1 py-1">
              <div
                className="grid gap-4"
                style={{ gridTemplateColumns: 'minmax(0, 1fr) minmax(14rem, 1.5fr) 7rem' }}
              >
                <div>
                  <label className="block text-sm mb-1">Prefix *</label>
                  <Input value={form.prefix} onChange={(e) => setField('prefix', e.target.value.toUpperCase())} placeholder="PLS" disabled={!!editId} />
                </div>
                <div>
                  <label className="block text-sm mb-1">Tier *</label>
                  <ReactSelect
                    value={selectedFormTierOption}
                    onChange={(option) => setField('tier', option?.value != null ? String(option.value) : '')}
                    options={formTierOptions}
                    isSearchable
                    isClearable={false}
                  />
                </div>
                <div>
                  <label className="block text-sm mb-1">Size order</label>
                  <Input
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    value={form.sizeOrder}
                    onChange={(e) => setSizeOrderField(e.target.value)}
                    placeholder="999"
                  />
                  <div className="mt-0.5 whitespace-nowrap text-[10px] text-gray-500">Smaller = earlier</div>
                </div>
              </div>

              <div>
                <label className="block text-sm mb-1 font-medium">This rule is a…</label>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant={form['flags.isAccessory'] ? 'outline' : 'default'}
                    size="sm"
                    onClick={() => setField('flags.isAccessory', false)}
                  >
                    Container
                  </Button>
                  <Button
                    type="button"
                    variant={form['flags.isAccessory'] ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setField('flags.isAccessory', true)}
                  >
                    Accessory
                  </Button>
                  <span className="self-center text-[11px] text-gray-500">
                    {form['flags.isAccessory']
                      ? 'An inner item — you set which containers it packs into.'
                      : 'A container — you set what packs inside it.'}
                  </span>
                </div>
              </div>

              {form['flags.isAccessory'] ? (
                <div>
                  <label className="mb-1 flex items-baseline gap-2 text-sm">
                    <span>Packs into</span>
                    <span className="text-[11px] text-gray-500">— containers that can hold this accessory.</span>
                  </label>
                  <PrefixMultiSelect
                    value={form.accessoryNesting}
                    onChange={(v) => setField('accessoryNesting', v)}
                    rules={rules}
                    prefixProducts={prefixProducts}
                    ariaLabel="Packs into"
                  />
                </div>
              ) : (
                <div>
                  <label className="mb-1 flex items-baseline gap-2 text-sm">
                    <span>Packs inside this</span>
                    <span className="text-[11px] text-gray-500">— items this container can hold.</span>
                  </label>
                  <PrefixMultiSelect
                    value={form.nestingRelationships}
                    onChange={(v) => setField('nestingRelationships', v)}
                    rules={rules}
                    prefixProducts={prefixProducts}
                    ariaLabel="Packs inside this"
                  />
                </div>
              )}

              <div>
                <label className="mb-1 flex items-baseline gap-2 text-sm">
                  <span>Cannot pack into (exceptions)</span>
                  <span className="text-[11px] text-gray-500">— containers this item is blocked from, even if otherwise allowed.</span>
                </label>
                <PrefixMultiSelect
                  value={form.nestingRestrictions}
                  onChange={(v) => setField('nestingRestrictions', v)}
                  rules={rules}
                  prefixProducts={prefixProducts}
                  ariaLabel="Cannot pack into"
                />
              </div>

              {!form['flags.isAccessory'] && (form.accessoryNesting || []).length ? (
                <div>
                  <label className="mb-1 flex items-baseline gap-2 text-sm">
                    <span>Extra containers this can pack into (advanced)</span>
                    <span className="text-[11px] text-gray-500">— usually only set on accessories; preserved here so it is not lost.</span>
                  </label>
                  <PrefixMultiSelect
                    value={form.accessoryNesting}
                    onChange={(v) => setField('accessoryNesting', v)}
                    rules={rules}
                    prefixProducts={prefixProducts}
                    ariaLabel="Extra containers this can pack into"
                  />
                </div>
              ) : null}

              {editId ? (() => {
                const inv = inverseRelationships(form.prefix, rules)
                return (
                  <div className="mt-1 rounded-md border border-gray-200 bg-slate-50 px-3 py-2 text-xs text-gray-600">
                    <div><span className="font-semibold">Containers that hold this:</span> {inv.heldBy.join(', ') || '—'}</div>
                    <div><span className="font-semibold">Accessories accepted here:</span> {inv.accessoriesAccepted.join(', ') || '—'}</div>
                    <div className="mt-1 text-[10px] text-gray-400">Derived from other rules — edit those rules to change.</div>
                  </div>
                )
              })() : null}

              <div>
                <label className="block text-sm mb-1 font-medium">Flags</label>
                <div
                  style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    columnGap: '1.5rem',
                    rowGap: '0.25rem',
                  }}
                >
                  {[
                    ['flags.cannotBeContained', 'Cannot be contained'],
                    ['flags.accessoriesOnlyContainer', 'Accessories-only container'],
                    ['flags.cannotBeNested', 'Cannot be nested'],
                    ['active', 'Active'],
                  ].map(([key, label]) => (
                    <label key={key} className="flex min-w-0 cursor-pointer items-center gap-2 text-sm">
                      <input type="checkbox" checked={!!form[key]} onChange={(e) => setField(key, e.target.checked)} className="shrink-0 rounded" />
                      <span className="min-w-0 whitespace-nowrap">{label}</span>
                    </label>
                  ))}
                </div>
              </div>

            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : 'Save'}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog
          open={deleteDialogOpen}
          onOpenChange={(open) => {
            setDeleteDialogOpen(open)
            if (!open) setDeleteTarget(null)
          }}
        >
          <DialogContent className="sm:max-w-[420px] bg-white text-black">
            <DialogHeader>
              <DialogTitle>Delete Nesting Rule</DialogTitle>
              <DialogDescription>
                Are you sure you want to delete this nesting rule? This action cannot be undone.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3 py-2 text-sm text-gray-700">
              {deleteTarget?.prefix ? (
                <div className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2">
                  <span className="text-gray-500">Prefix:</span>
                  {' '}
                  <span className="font-mono font-semibold text-black">{deleteTarget.prefix}</span>
                </div>
              ) : null}
            </div>
            <DialogFooter>
              <DialogClose asChild>
                <Button variant="outline" disabled={deleting}>Cancel</Button>
              </DialogClose>
              <Button variant="destructive" onClick={confirmDelete} disabled={deleting}>
                {deleting ? 'Deleting...' : 'Delete'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog
          open={seedDialogOpen}
          onOpenChange={(open) => {
            if (!seeding) setSeedDialogOpen(open)
          }}
        >
          <DialogContent className="sm:max-w-[460px] bg-white text-black">
            <DialogHeader>
              <DialogTitle>Seed default rules</DialogTitle>
              <DialogDescription>
                Copies the backend built-in default rules into the editable database set. Database rules that are not in the defaults will be removed. This repopulates the editable set — it does not change the built-in source itself.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <DialogClose asChild>
                <Button variant="outline" disabled={seeding}>Cancel</Button>
              </DialogClose>
              <Button onClick={confirmSeed} disabled={seeding}>
                {seeding ? 'Seeding...' : 'Seed'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  )
}

export default NestingRulesPage
